import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, SessionInformationService } from 'app/core/core.module';
import { Order, PurchaseOrder, PurchaseOrderStatus } from 'app/core/model/entity-model';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import { VendorPickupDialogComponent } from '../vendor-pickup-dialog/vendor-pickup-dialog.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import query from 'devextreme/data/query';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import moment from 'moment';

@Component({
    selector: 'app-purchase-order-search',
    templateUrl: './purchase-order-search.component.html',
    styleUrls: ['./purchase-order-search.component.scss'],
    standalone: false,
})
export class PurchaseOrderSearchComponent implements OnInit {
    purchaseOrders: any;
    filter: any;
    openTotal: any;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: GenesisUnitOfWork,
        private router: Router,
        public auth: AuthService,
        private appInfo: AppInformationService,
        private sessionInfo: SessionInformationService,
        private modalService: BsModalService
    ) {}

    async ngOnInit() {
        this.filter = this.sessionInfo.getItem('purchase-order-search-filter') ?? { showCompleted: false };
        await this.getData();
    }

    async getData() {
        this.purchaseOrders = (await this.uow.getPurchaseOrders(this.filter.showCompleted, ['vendor', 'status'])) as PurchaseOrder[];

        const pickups = await this.uow.getVendorPickUpInfos(false);
        this.purchaseOrders.forEach((po) => {
            const poPickups = pickups.filter((pickup) =>
                pickup.lines.some((line) => line.purchaseOrderLine.purchaseOrder.id === po.id)
            );
            po.pickups = poPickups.map((p) => p.id);
        });

        const openInfos = await this.uow.getOpenPurchaseOrderInfos();
        this.purchaseOrders.forEach((po) => {
            const openInfo = openInfos.find((info) => info.id === po.id);
            if (openInfo) {
                po._openBalance = openInfo.openBalance;
            }
        });
    }

    setOpenTotal() {
        const filterExpr = this.dataGrid.instance.getCombinedFilter();
        const filteredData = query(this.purchaseOrders).filter(filterExpr).toArray();
        this.openTotal = _.sum(filteredData.filter((x) => x.statusId === PurchaseOrder.Statuses.Receiving).map((order) => order.total));
    }

    async toggleCompleted() {
        this.filter.showCompleted = !this.filter.showCompleted;
        await this.getData();
        this.sessionInfo.setItem('purchase-order-search-filter', this.filter);
    }

    // print() {
    //     const selectedInvoices = this.dataGrid.instance.getSelectedRowsData();
    //     if (selectedInvoices.length === 0) return;
    //     const invoiceIds = selectedInvoices.map((x) => {
    //         return x.id;
    //     });
    //     this.reportService.openSalesReport(invoiceIds);
    // }

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.dataGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.dataGrid.instance.clearSelection();
        }
        this.setOpenTotal();
    }

    selectionChange(e) {}

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            }
        );
    }

    async open(orderId: number) {
        this.router.navigate(['/purchase-orders/' + (!!orderId ? orderId : 'new')]);
    }

    async rowDoubleClick(e) {
        if (!e.data || !e.data.id) return;
        await this.open(e.data.id);
    }

    async delete(selection: any) {
        const result = await (confirm('Are you sure you want to DELETE this purchase order?', `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (!result) return;

        if (selection.statusId === PurchaseOrder.Statuses.New) {
            selection.deleted = new Date();
                selection.deletedById = this.auth.currentUser.id;
            await this.uow.commit();
            this.getData();
            return;
        }

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Enter Reason For Deletion',
                dialogPrompt: 'Enter Reason For Deletion',
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
            if (result.result && !!result.value && result.value.length > 0) {
                selection.deleted = new Date();
                selection.deletedById = this.auth.currentUser.id;
                const note = `Deleted ${moment(result.deleted).format('MM/DD/YYYY')} by ${this.auth.currentUser.userName}: ${result.value}`;
                selection.note = selection.note ? selection.note + '\n' + note : note;
                await this.uow.commit();
                this.getData();
            } else {
                await alert('Reason required.', this.appInfo.settings.company);
            }
        });
    }

    async updateStatus() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        const result = await (confirm(
            `Are you sure you want to COMPLETE ${selectedRows.length === 1 ? 'this order' : 'these orders'}?`,
            `${this.appInfo.settings.company}`
        ) as Promise<boolean>);
        if (!result) return;

        const orderIds = selectedRows.map((x) => {
            return x.id;
        });

        // need to check orderline details for open ones
        const buildDetails = ((await this.uow.getBuildDetailsByOrderIds(orderIds)) as any).filter((x) => {
            return x.status.name !== Order.Statuses.Complete;
        });

        if (buildDetails.length > 0) {
            // tslint:disable-next-line:no-shadowed-variable
            const result = await (confirm(
                `There are Build Details not marked as COMPLETE for one or more selected rows.  Do you want to Complete them now?`,
                `${this.appInfo.settings.company}`
            ) as Promise<boolean>);
            if (!result) return;
            else {
                buildDetails.forEach((detail) => {
                    detail.statusId = 4;
                });
            }
        }

        selectedRows.forEach((row) => {
            row.statusId = Order.Statuses.Complete;
        });
        await this.uow.commit();
        await this.getData();
    }

    async createPickup(purchaseOrders: PurchaseOrder[]) {
        if (!purchaseOrders || purchaseOrders.length === 0) return;

        const hasSameVendor = purchaseOrders.every((p) => p.vendorId === purchaseOrders[0].vendorId);
        if (!hasSameVendor) {
            await alert('All purchase orders must be for the same vendor', this.appInfo.settings.company);
            return;
        }

        const ids = purchaseOrders.map((po) => po.id);
        const lines = await this.uow.getPurchaseOrderLines(ids, 'purchaseOrder.vendor, pickUps');
        const modalRef = this.modalService.show(VendorPickupDialogComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                purchaseOrderLines: lines,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }
}
